import { useState, useMemo } from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Eye, EyeOff, Check, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { getPasswordStrength, validatePassword } from "@/utils/formValidation";
import { useLanguage } from "@/context/LanguageContext";
import { authLabels } from "@/i18n";

interface PasswordInputProps {
    id: string;
    label: string;
    value: string;
    onChange: (value: string) => void;
    placeholder?: string;
    required?: boolean;
    showStrength?: boolean;
    showRequirements?: boolean;
    className?: string;
    validator?: (value: string) => { isValid: boolean; errors: string[] };
    showErrors?: boolean;
    autoComplete?: string;
}

export const PasswordInput = ({
    id,
    label,
    value,
    onChange,
    placeholder = "••••••••",
    required = false,
    showStrength = true,
    showRequirements = true,
    className = "",
    validator,
    showErrors = true,
    autoComplete = "new-password",
}: PasswordInputProps) => {
    const { language } = useLanguage();
    const labels = useMemo(() => authLabels(language)?.forms?.validation || {}, [language]);
    const [showPassword, setShowPassword] = useState(false);
    const [isFocused, setIsFocused] = useState(false);
    const [isTouched, setIsTouched] = useState(false);

    const validation = validatePassword(value, {
      minLengthError: labels.minLengthError,
      hasLowercaseError: labels.hasLowercaseError,
      hasUppercaseError: labels.hasUppercaseError,
      hasNumberError: labels.hasNumberError,
      hasSpecialCharError: labels.hasSpecialCharError,
    });
    const strength = getPasswordStrength(value);

    const customValidation = validator ? validator(value) : { isValid: true, errors: [] };
    const hasCustomError = customValidation.errors.length > 0 && (isTouched || value.length > 0);

    const getStrengthColor = () => {
        switch (strength) {
            case 'weak': return 'bg-red-500';
            case 'medium': return 'bg-yellow-500';
            case 'strong': return 'bg-green-500';
            default: return 'bg-gray-300';
        }
    };

    const getStrengthText = () => {
        switch (strength) {
            case 'weak': return labels.strengthWeak || 'Weak';
            case 'medium': return labels.strengthMedium || 'Medium';
            case 'strong': return labels.strengthStrong || 'Strong';
            default: return '';
        }
    };

    const handleBlur = () => {
        setIsTouched(true);
        setIsFocused(false);
    };

    return (
        <div className={`space-y-2 ${className}`}>
            <Label htmlFor={id}>
                {label} {required && <span className="text-red-500">*</span>}
            </Label>

            <div className="relative">
                <Input
                    id={id}
                    type={showPassword ? "text" : "password"}
                    autoComplete={autoComplete}
                    placeholder={placeholder}
                    value={value}
                    onChange={(e) => onChange(e.target.value)}
                    onFocus={() => setIsFocused(true)}
                    onBlur={handleBlur}
                    className={`pr-10 ${(validation.errors.length > 0 && value.length > 0) || hasCustomError ? 'border-red-500' : ''}`}
                />
                <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
                    onClick={() => setShowPassword(!showPassword)}
                >
                    {showPassword ? (
                        <EyeOff className="h-4 w-4" />
                    ) : (
                        <Eye className="h-4 w-4" />
                    )}
                </Button>
            </div>

            {showErrors && hasCustomError && (
                <div className="space-y-1">
                    {customValidation.errors.map((error, index) => (
                        <p key={index} className="text-red-500 text-xs">{error}</p>
                    ))}
                </div>
            )}

            {showStrength && value.length > 0 && !validator && (
                <div className="space-y-2">
                    <div className="flex items-center gap-2">
                        <div className="flex-1 h-2 bg-gray-200 rounded-full overflow-hidden">
                            <div
                                className={`h-full transition-all duration-300 ${getStrengthColor()}`}
                                style={{
                                    width: strength === 'weak' ? '25%' : strength === 'medium' ? '60%' : '100%'
                                }}
                            />
                        </div>
                        <span className="text-sm text-gray-600">{getStrengthText()}</span>
                    </div>
                </div>
            )}

            {showRequirements &&
              value.length > 0 &&
              !validator &&
              (isFocused || !validation.isValid) && (
                <div className="space-y-1">
                    <p className="text-sm font-medium text-gray-700">{labels.requirementsHeading || 'Password requirements:'}</p>
                    <div className="space-y-1">
                        <div className="flex items-center gap-2 text-sm">
                            {validation.requirements.minLength ? (
                                <Check className="h-3 w-3 text-green-500" />
                            ) : (
                                <X className="h-3 w-3 text-red-500" />
                            )}
                            <span className={validation.requirements.minLength ? 'text-green-600' : 'text-red-600'}>
                                {labels.minLength || 'At least 8 characters'}
                            </span>
                        </div>
                        <div className="flex items-center gap-2 text-sm">
                            {validation.requirements.hasLowercase ? (
                                <Check className="h-3 w-3 text-green-500" />
                            ) : (
                                <X className="h-3 w-3 text-red-500" />
                            )}
                            <span className={validation.requirements.hasLowercase ? 'text-green-600' : 'text-red-600'}>
                                {labels.hasLowercase || 'At least one lowercase letter'}
                            </span>
                        </div>
                        <div className="flex items-center gap-2 text-sm">
                            {validation.requirements.hasUppercase ? (
                                <Check className="h-3 w-3 text-green-500" />
                            ) : (
                                <X className="h-3 w-3 text-red-500" />
                            )}
                            <span className={validation.requirements.hasUppercase ? 'text-green-600' : 'text-red-600'}>
                                {labels.hasUppercase || 'At least one uppercase letter'}
                            </span>
                        </div>
                        <div className="flex items-center gap-2 text-sm">
                            {validation.requirements.hasNumber ? (
                                <Check className="h-3 w-3 text-green-500" />
                            ) : (
                                <X className="h-3 w-3 text-red-500" />
                            )}
                            <span className={validation.requirements.hasNumber ? 'text-green-600' : 'text-red-600'}>
                                {labels.hasNumber || 'At least one digit'}
                            </span>
                        </div>
                        <div className="flex items-center gap-2 text-sm">
                            {validation.requirements.hasSpecialChar ? (
                                <Check className="h-3 w-3 text-green-500" />
                            ) : (
                                <X className="h-3 w-3 text-red-500" />
                            )}
                            <span className={validation.requirements.hasSpecialChar ? 'text-green-600' : 'text-red-600'}>
                                {labels.hasSpecialChar || 'At least one special character'}
                            </span>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}; 